import { isEmpty } from "lodash";
import Link from "next/link";
import { FC, useMemo } from "react";
import Container from "~/components/Container";
import SectionTitle from "~/components/SectionTitle";
import Colors from "~/constants/colors";
import { Product } from "~/services/product";
import ProductsSlider from "../../components/ProductsSlider";
import styles from "./RecentlyView.module.scss";
import { ProductListKey } from "~/constants/enum";
import { getLocalStorage, LocalStorageKey } from "~/utils/local-storage";
import useClient from "~/hooks/useClient";
import { useRecentlyViewProducts } from "~/data/home";
import { RECENTLY_PRODUCT } from "~/constants/localstore";
import { useQuery } from "@apollo/client";
import { GET_PRODUCTS_TRACKING } from "~/data/product/gql";

type Banner = {
  image?: string;
  url?: string;
};
interface RecentlyViewProps {
  title?: string;
  image?: string;
  data?: Product[];
  banner?: {
    image?: string;
    url?: string;
  };
  skip?: boolean;
  numberOfItems?: number;
}

const RecentlyView: FC<RecentlyViewProps> = ({
  title = "Based on your recent views",
  image,
  data = [],
  banner,
  skip,
  numberOfItems = 4,
}) => {
  const isHasRecently = getLocalStorage(LocalStorageKey.RECENTLY_PRODUCT);
  const recentlyProduct =
    typeof window !== "undefined" && localStorage.getItem(RECENTLY_PRODUCT);
  const { productItems, isFetched } = useRecentlyViewProducts({
    defaultProductIds:
      typeof window !== "undefined" ? JSON.parse(recentlyProduct || "[]") : [],
  });
  const fullRecentlyProducts = getLocalStorage(
    LocalStorageKey.FULL_RECENTLY_PRODUCT,
  );
  const isClient = useClient();

  // const { loading, isFetched, productItems } = useRecentlyViewProducts({ skip });

  const _banner: Banner | null = useMemo(() => {
    if (!skip && banner) {
      return banner;
    }
    return null;
  }, [banner, skip]);

  const { data: dataCollectionTracking, loading: loadingCollectionTracking } =
    useQuery(GET_PRODUCTS_TRACKING, {
      variables: {
        skus: productItems.map((product) => product.sku + ""),
      },
      context: { fetchOptions: { method: "GET" } },
      skip: !productItems.length,
    });

  const _data: Partial<Product>[] = useMemo(() => {
    if (!!skip) {
      return data;
    }
    if (!!data.length) return data;
    if (
      !!isFetched &&
      productItems &&
      productItems.length > 0 &&
      loadingCollectionTracking
    )
      return productItems;
    if (!!isFetched && productItems && productItems.length > 0) {
      return productItems.map((product: Product) => {
        const tracking = dataCollectionTracking?.productsBySkus?.items?.find(
          (item: any) => item.sku === product.sku,
        );
        return {
          ...product,
          ...tracking,
        };
      });
    }
    if (!!fullRecentlyProducts) return fullRecentlyProducts;
  }, [
    skip,
    data,
    fullRecentlyProducts,
    productItems,
    isFetched,
    loadingCollectionTracking,
  ]);

  if (!isClient || (isEmpty(_data) && isEmpty(_banner)) || !isHasRecently) {
    return null;
  }

  return (
    <Container
      rootClassName={styles.recently_view_wrapper}
      className="flex flex-col"
    >
      <SectionTitle text={title} dividerColor={Colors.DIVIDER_COLOR} />

      {_banner && (
        <Link href={_banner.url || "#"}>
          <div className="relative w-full aspect-[1296/400] lg:mb-5">
            <img
              className="object-cover"
              src={_banner.image || "/images/clearance.png"}
              alt="Clearance"
              // fill
              // priority={false}
            />
          </div>
        </Link>
      )}
      <ProductsSlider
        products={_data}
        title=""
        listkey={ProductListKey.RecentlyView}
      />
    </Container>
  );
};

export default RecentlyView;
